import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import NotificationBell from "@/components/NotificationBell";
import ThemeSwitcher from "@/components/ThemeSwitcher";

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [communityDropdownOpen, setCommunityDropdownOpen] = useState(false);
  const [communityDropdownPos, setCommunityDropdownPos] = useState({ top: 0, left: 0 });
  const [user, setUser] = useState(null);
  const dropdownRef = useRef(null);
  const communityBtnRef = useRef(null);
  const { pageStyles } = useTheme();

  useEffect(() => {
    // Get user from localStorage/sessionStorage
    const loadUser = () => {
      const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };
    loadUser();

    // Listen for login/logout events
    window.addEventListener('userLogin', loadUser);
    window.addEventListener('storage', loadUser);
    return () => {
      window.removeEventListener('userLogin', loadUser);
      window.removeEventListener('storage', loadUser);
    };
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideButton = communityBtnRef.current && !communityBtnRef.current.contains(event.target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      if (isOutsideButton && isOutsideDropdown) {
        setCommunityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (communityDropdownOpen && communityBtnRef.current) {
      const rect = communityBtnRef.current.getBoundingClientRect();
      setCommunityDropdownPos({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
  }, [communityDropdownOpen]);

  const userRole = user?.role || null;

  // Build community sub-items based on user role
  const getCommunitySubItems = () => {
    const items = [];
    
    // Community Hub - visible to everyone except dive_center
    if (userRole !== "dive_center") {
      items.push({ label: "Community Hub", path: "/community" });
    }
    
    // Marine Database - visible to all logged-in users except dive_center
    if (userRole && userRole !== "dive_center") {
      items.push({ label: "Marine Database", path: "/community/marine-database" });
    }
    
    // Forum - visible to all logged-in users except dive_center
    if (userRole && userRole !== "dive_center") {
      items.push({ label: "Forum", path: "/community/forum" });
    }
    
    // Pros - visible to all logged-in users
    if (userRole) {
      items.push({ label: "Pros", path: "/community/pros" });
    }
    
    // Dive Centers (Recruiting) - visible to pros and admin only
    if (userRole === "pro" || userRole === "admin") {
      items.push({ label: "Dive Centers", path: "/community/dive-centers" });
    }
    
    return items;
  };

  const communitySubItems = getCommunitySubItems();

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Map", path: "/map" },
    { label: "Dive Centers", path: "/dive-centers" },
    { label: "Dive Catalogue", path: "/packages" },
    { label: "Gallery", path: "/gallery" },
    { label: "Reviews", path: "/reviews" },
    { label: "Contact", path: "/contact" }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setCommunityDropdownOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isCommunityActive = () => {
    return location.pathname.startsWith("/community");
  };

  // Get current page theme
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/map') return 'map';
    if (path === '/dive-centers' || path.includes('/dive-center')) return 'dive_centers';
    if (path === '/packages') return 'packages';
    if (path === '/gallery') return 'gallery';
    if (path === '/reviews') return 'reviews';
    if (path === '/contact') return 'contact';
    return 'home'; // fallback
  };
  
  const currentPage = getCurrentPageName();
  const navTheme = pageStyles[currentPage]?.navbar;
  
  // Apply theme or use defaults
  const navBgValue = navTheme?.background_value || "rgba(255, 255, 255, 0.95)";
  const navTextColor = navTheme?.text_color || "#1e293b";
  const accentColor = navTheme?.accent_color || "#0891b2";
  const accentSecondary = navTheme?.accent_color_secondary || accentColor;
  const isOverlay = navTheme?.nav_overlay === true;
  const borderColor = navTheme?.border_color || "rgba(229, 231, 235, 1)";
  
  // Detect if this is a dark-themed nav
  const isDarkNav = navTextColor === "#e2e8f0" || navTextColor === "#ffffff" || navTextColor === "#f8fafc";
  
  const navStyle = {
    background: navBgValue,
    color: navTextColor,
    borderColor: borderColor
  };

  // Dropdown style adapts to dark/light nav
  const dropdownBg = isDarkNav ? "rgba(15, 15, 30, 0.95)" : "#ffffff";
  const dropdownHoverBg = isDarkNav ? "rgba(62, 201, 224, 0.1)" : "#f3f4f6";
  const dropdownTextColor = isDarkNav ? "#cbd5e1" : "#374151";

  // Determine if we should show the community dropdown or single link
  const showCommunityDropdown = communitySubItems.length > 1;

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-all duration-300`}
      style={navStyle}
      data-testid="navigation"
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <div 
          className="flex items-center gap-4 cursor-pointer" 
          onClick={() => handleNavigation("/")}
        >
          <img 
            src="/george-dives-logo.webp" 
            alt="George Dives Logo" 
            className="h-20 w-20 rounded-full"
            data-testid="logo"
          />
          <span className="text-2xl font-bold" style={{ color: navTextColor }}>George Dives</span>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className="font-medium transition-all duration-200 relative group"
              style={{ 
                color: isActive(item.path) ? accentColor : navTextColor,
                opacity: isActive(item.path) ? 1 : 0.8,
                fontWeight: isActive(item.path) ? 700 : 500
              }}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              {item.label}
              <span 
                className="absolute -bottom-1 left-0 h-0.5 transition-all duration-200 rounded-full"
                style={{ 
                  width: isActive(item.path) ? '100%' : '0%',
                  background: `linear-gradient(to right, ${accentColor}, ${accentSecondary})`
                }}
              />
            </button>
          ))}
          
          {/* Community with dropdown */}
          {showCommunityDropdown ? (
            <>
              <button
                ref={communityBtnRef}
                onClick={() => setCommunityDropdownOpen(!communityDropdownOpen)}
                className="font-medium transition-all duration-200 flex items-center gap-1 relative group"
                style={{ 
                  color: isCommunityActive() ? accentColor : navTextColor,
                  opacity: isCommunityActive() ? 1 : 0.8,
                  fontWeight: isCommunityActive() ? 700 : 500
                }}
                data-testid="nav-community"
              >
                Community
                <ChevronDown className={`w-4 h-4 transition-transform ${communityDropdownOpen ? 'rotate-180' : ''}`} />
                <span 
                  className="absolute -bottom-1 left-0 h-0.5 transition-all duration-200 rounded-full"
                  style={{ 
                    width: isCommunityActive() ? '100%' : '0%',
                    background: `linear-gradient(to right, ${accentColor}, ${accentSecondary})`
                  }}
                />
              </button>
              
              {communityDropdownOpen && createPortal(
                <div 
                  ref={dropdownRef}
                  className="fixed w-48 rounded-lg shadow-xl border py-2 z-[10200] backdrop-blur-xl"
                  style={{ 
                    top: `${communityDropdownPos.top}px`, 
                    left: `${communityDropdownPos.left}px`,
                    background: dropdownBg,
                    borderColor: borderColor
                  }}
                >
                  {communitySubItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className="w-full text-left px-4 py-2 transition-colors"
                      style={{ 
                        color: isActive(item.path) ? accentColor : dropdownTextColor,
                        fontWeight: isActive(item.path) ? 700 : 400,
                        backgroundColor: isActive(item.path) ? (isDarkNav ? 'rgba(62,201,224,0.08)' : '#ecfeff') : 'transparent'
                      }}
                      onMouseEnter={(e) => { if (!isActive(item.path)) e.target.style.backgroundColor = dropdownHoverBg; }}
                      onMouseLeave={(e) => { if (!isActive(item.path)) e.target.style.backgroundColor = 'transparent'; }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </>
          ) : (
            <button
              onClick={() => handleNavigation("/community")}
              className="font-medium transition-all duration-200 relative group"
              style={{ 
                color: isCommunityActive() ? accentColor : navTextColor,
                opacity: isCommunityActive() ? 1 : 0.8,
                fontWeight: isCommunityActive() ? 700 : 500
              }}
              data-testid="nav-community"
            >
              Community
              <span 
                className="absolute -bottom-1 left-0 h-0.5 transition-all duration-200 rounded-full"
                style={{ 
                  width: isCommunityActive() ? '100%' : '0%',
                  background: `linear-gradient(to right, ${accentColor}, ${accentSecondary})`
                }}
              />
            </button>
          )}
        </div>

        {/* Notification Bell - Desktop */}
        {user && user.role !== "dive_center" && (
          <div className="hidden lg:block">
            <NotificationBell />
          </div>
        )}

        {/* Theme Switcher - Desktop (right before mobile menu, after nav links) */}
        <div className="hidden lg:flex items-center ml-2">
          <ThemeSwitcher
            mode="desktop"
            tone={isDarkNav ? 'dark' : 'light'}
            accentColor={accentColor}
          />
        </div>

        {/* Mobile right cluster: theme switcher + hamburger (always visible side-by-side) */}
        <div className="lg:hidden flex items-center gap-2">
          <ThemeSwitcher
            mode="mobile"
            tone={isDarkNav ? 'dark' : 'light'}
            accentColor={accentColor}
          />
          <button 
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            style={{ color: navTextColor }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden border-t shadow-lg max-h-[calc(100vh-80px)] overflow-y-auto backdrop-blur-xl" 
          style={{ background: isDarkNav ? 'rgba(10,10,20,0.95)' : navBgValue, borderColor }}
          data-testid="mobile-menu"
        >
          <div className="container mx-auto px-4 py-4 flex flex-col gap-4 pb-8">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className="text-left font-medium py-2 transition-colors"
                style={{ 
                  color: isActive(item.path) ? accentColor : navTextColor,
                  opacity: isActive(item.path) ? 1 : 0.8,
                  fontWeight: isActive(item.path) ? 700 : 500
                }}
              >
                {item.label}
              </button>
            ))}
            
            {/* Notification Bell - Mobile */}
            {user && user.role !== "dive_center" && (
              <div className="border-t pt-2 mt-2" style={{ borderColor }} data-testid="mobile-notification-bell">
                <NotificationBell />
              </div>
            )}

            {/* Community section in mobile */}
            {showCommunityDropdown ? (
              <div className="border-t pt-2 mt-2" style={{ borderColor }}>
                <p className="font-semibold text-sm mb-2" style={{ color: accentSecondary, opacity: 0.7 }}>Community</p>
                {communitySubItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className="w-full text-left font-medium py-2 pl-4 transition-colors"
                    style={{ 
                      color: isActive(item.path) ? accentColor : navTextColor,
                      opacity: isActive(item.path) ? 1 : 0.8
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : (
              <button
                onClick={() => handleNavigation("/community")}
                className="text-left font-medium py-2 transition-colors"
                style={{ 
                  color: isCommunityActive() ? accentColor : navTextColor,
                  opacity: isCommunityActive() ? 1 : 0.8
                }}
              >
                Community
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
